import { Check } from "lucide-react"
import type React from "react"
import { useMemo } from "react"
import { cn } from "../lib/utils"
import type { STAGE, StageConfiguration } from "../types/form-types"
import { Button } from "./ui/button"
import { useLogicStore } from "@/stores/use-conditional-logic-store"

export interface StageNavigatorProps {
	stages: STAGE[]
	activeIndex: number
	config?: StageConfiguration
	onStageChange: (index: number) => void
}

export const StageNavigator: React.FC<StageNavigatorProps> = ({
	stages,
	activeIndex,
	config,
	onStageChange,
}) => {
	const { alignment = "TOP", type = "TITLES_ONLY" } = config || {}
	const { hiddenFields } = useLogicStore()

	const filteredStages = useMemo(
		() => stages.filter(stage => !hiddenFields[stage.stage_id]),
		[stages, hiddenFields],
	)

	const sortedStages = useMemo(
		() => [...filteredStages].sort((a, b) => a.order_index - b.order_index),
		[filteredStages],
	)

	const isVertical = alignment === "LEFT" || alignment === "RIGHT"

	const getStepState = (
		index: number,
	): "completed" | "active" | "inactive" => {
		if (index < activeIndex) return "completed"
		if (index === activeIndex) return "active"
		return "inactive"
	}

	const handleStageClick = (index: number) => {
		const state = getStepState(index)
		const isNavigationAllowed =
			index <= activeIndex || state === "completed"
		if (isNavigationAllowed && index !== activeIndex) {
			onStageChange(index)
		}
	}

	if (isVertical) {
		return (
			<div className="df-stage-navigator-vertical w-80 flex-shrink-0">
				<div className="sticky top-6">
					<div className="relative space-y-1 p-4">
						{sortedStages.map((stage, index) => {
							const state = getStepState(index)
							const isNavigationAllowed =
								index <= activeIndex || state === "completed"

							return (
								<div
									key={stage.stage_id}
									className="relative"
								>
									<Button
										type="button"
										variant="ghost"
										disabled={!isNavigationAllowed}
										onClick={() => handleStageClick(index)}
										className={cn(
											"h-auto min-h-[60px] w-full justify-start gap-3 p-3",
											"!opacity-100 transition-all duration-200",
											{
												"cursor-not-allowed":
													!isNavigationAllowed,
											},
										)}
									>
										<div
											className={cn(
												"z-20 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-lg font-medium",
												{
													"text-primary border-primary bg-background border":
														state === "active",
													"bg-primary text-primary-foreground":
														state === "completed",
													"bg-muted text-muted-foreground opacity-100":
														state === "inactive",
												},
											)}
										>
											{state === "completed" ? (
												<Check className="h-4 w-4" />
											) : (
												<span>{index + 1}</span>
											)}
										</div>

										<div className="flex min-w-0 flex-1 flex-col items-start text-left">
											<div
												className={cn(
													"text-base font-semibold leading-tight",
													{
														"text-foreground":
															state === "active",
														"text-foreground opacity-50":
															state ===
															"completed",
														"text-muted-foreground font-normal":
															state ===
															"inactive",
													},
												)}
											>
												{stage.name}
											</div>

											{type ===
												"TITLES_AND_DESCRIPTIONS" &&
												stage.description && (
													<div
														className={cn(
															"mt-1 text-xs font-normal italic leading-tight",
															{
																"text-foreground":
																	state ===
																	"active",
																"text-foreground opacity-50":
																	state ===
																	"completed",
																"text-muted-foreground font-normal":
																	state ===
																	"inactive",
															},
														)}
													>
														{stage.description}
													</div>
												)}
										</div>
									</Button>

									{/* Connection line */}
									{index < sortedStages.length - 1 && (
										<div className="bg-muted absolute left-7 top-12 z-10 h-full w-0.5" />
									)}
								</div>
							)
						})}
					</div>
				</div>
			</div>
		)
	}

	// Horizontal layout
	return (
		<div className="df-stage-navigator-horizontal mb-8">
			<div className="relative flex justify-center">
				<div className="flex items-center">
					{sortedStages.map((stage, index) => {
						const state = getStepState(index)
						const isNavigationAllowed =
							index <= activeIndex || state === "completed"

						return (
							<div
								key={stage.stage_id}
								className="relative flex items-center"
							>
								<Button
									type="button"
									variant="ghost"
									disabled={!isNavigationAllowed}
									onClick={() => handleStageClick(index)}
									className={cn(
										"flex h-auto min-h-[80px] min-w-24 max-w-[200px] flex-col items-center gap-2 p-4",
										"!opacity-100 transition-all duration-200",
										{
											"cursor-not-allowed":
												!isNavigationAllowed,
										},
									)}
								>
									<div
										className={cn(
											"z-20 flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium",
											{
												"text-primary border-primary bg-background border":
													state === "active",
												"bg-primary text-primary-foreground":
													state === "completed",
												"bg-muted text-muted-foreground opacity-100":
													state === "inactive",
											},
										)}
									>
										{state === "completed" ? (
											<Check className="h-5 w-5" />
										) : (
											<span>{index + 1}</span>
										)}
									</div>

									<div className="text-center">
										<div
											className={cn(
												"text-sm font-medium",
												{
													"text-foreground":
														state === "active",
													"text-foreground opacity-50":
														state === "completed",
													"text-muted-foreground font-normal":
														state === "inactive",
												},
											)}
										>
											{stage.name}
										</div>

										{type === "TITLES_AND_DESCRIPTIONS" &&
											stage.description && (
												<div
													className={cn(
														"mt-1 text-xs",
														{
															"text-foreground":
																state ===
																"active",
															"text-foreground opacity-50":
																state ===
																"completed",
															"text-muted-foreground font-normal":
																state ===
																"inactive",
														},
													)}
												>
													{stage.description}
												</div>
											)}
									</div>
								</Button>

								{/* Connection line */}
								{index < sortedStages.length - 1 && (
									<div className="bg-border absolute left-14 top-9 z-10 mx-2 h-0.5 w-full flex-1">
										<div
											className={cn(
												"bg-primary h-full transition-all duration-300",
												{
													"w-full":
														index < activeIndex,
													"w-0": index >= activeIndex,
												},
											)}
										/>
									</div>
								)}
							</div>
						)
					})}
				</div>
			</div>
		</div>
	)
}
